import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import FanfixHero from './components/FanfixHero';
import InstallPwaModal from '@/components/InstallPwaModal';

// ── Platform Creator Types + Who We're Building For (merged) ─────────────────
function PlatformCreatorSection({ onGetStarted }: { onGetStarted: () => void }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { setVisible(entry.isIntersecting); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadVideo = async () => {
      try {
        const response = await fetch('https://storage.helloreaddy.io/project_files/f4f0a068-4d6e-4d8b-b9c5-f019c11b8e5c/27a6a12e-ebb6-45ca-8df1-3da5fb4492e2_Smugg.mp4');
        if (!response.ok) throw new Error('Failed to fetch');
        const blob = await response.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setVideoSrc(url);
        setVideoLoading(false);
        setIsPlaying(true); // start playing immediately once loaded
      } catch {
        if (!cancelled) {
          setVideoError(true);
          setVideoLoading(false);
        }
      }
    };
    loadVideo();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;
    video.play().catch(() => {
      // auto-play blocked — video will just sit paused
    });
  }, [videoSrc]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const handleVideoEnded = () => setIsPlaying(false);

  const creatorTypes = [
    { label: 'CREATORS',   color: '#a855f7' },  // purple-500
    { label: 'ARTISTS',    color: '#c026d3' },  // fuchsia-600
    { label: 'Podcasters',     color: '#ec4899' },  // pink-500
    { label: 'STREAMERS',  color: '#f43f5e' },  // rose-500
    { label: 'Short Series', color: '#fb923c' },  // orange-400
    { label: 'MODELS',     color: '#f97316' },  // orange-500
    { label: 'COMEDIANS',  color: '#ec4899' },  // pink-500
    { label: 'YOUTUBERS',  color: '#a855f7' },  // purple-500
  ];

  const creatorNiches = [
    { icon: 'ri-video-line',      gradient: 'from-red-500 to-pink-500',     title: 'Video Creators',     description: 'YouTubers, TikTokers, vloggers, and video content producers',          examples: 'Tutorials, vlogs, reviews, entertainment' },
    { icon: 'ri-music-line',      gradient: 'from-purple-500 to-violet-500', title: 'Artists & Musicians', description: 'Digital artists, illustrators, musicians, and performers',              examples: 'Art, music, performances, behind-the-scenes' },
    { icon: 'ri-book-open-line',  gradient: 'from-orange-500 to-amber-500', title: 'Educators & Coaches', description: 'Teachers, fitness coaches, life coaches, and mentors',                 examples: 'Courses, coaching, workshops, guidance' },
    { icon: 'ri-gamepad-line',    gradient: 'from-green-500 to-teal-500',   title: 'Gamers & Streamers',  description: 'Gaming content creators, streamers, and esports personalities',       examples: 'Gameplay, streams, tips, community' },
    { icon: 'ri-quill-pen-line',  gradient: 'from-cyan-500 to-sky-500',     title: 'Writers & Bloggers',  description: 'Authors, journalists, bloggers, and storytellers',                    examples: 'Articles, stories, newsletters, essays' },
    { icon: 'ri-camera-line',     gradient: 'from-pink-500 to-rose-500',    title: 'Photographers',       description: 'Professional and hobbyist photographers sharing their work',          examples: 'Photos, presets, tutorials, portfolios' },
    { icon: 'ri-restaurant-line', gradient: 'from-amber-500 to-orange-500', title: 'Food & Lifestyle',    description: 'Chefs, food bloggers, lifestyle influencers, and wellness creators',   examples: 'Recipes, lifestyle tips, wellness content' },
    { icon: 'ri-lightbulb-line',  gradient: 'from-teal-500 to-emerald-500', title: 'Entrepreneurs',       description: 'Business owners, consultants, and thought leaders',                   examples: 'Business tips, consulting, expertise sharing' },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden"
      style={{ background: 'transparent' }}
    >




      {/* ── TOP: phone mockup + animated creator type words — centred ── */}
      <div className="relative z-10 w-full px-6 lg:px-16 pt-20 pb-16 flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-20">

        {/* Phone mockup */}
        <div
          className="flex-shrink-0 flex justify-center"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(-50px)',
            transition: 'opacity 0.7s ease, transform 0.7s cubic-bezier(0.34,1.3,0.64,1)',
          }}
        >
          <div
            className="relative w-[260px] sm:w-[300px]"
            style={{
              borderRadius: '2.5rem',
              border: '2.5px solid rgba(180,140,255,0.45)',
              overflow: 'hidden',
              boxShadow: '0 0 60px rgba(140,80,255,0.25), 0 20px 60px rgba(0,0,0,0.5)',
            }}
          >
            <div className="relative aspect-[9/16] bg-gray-900 overflow-hidden">
              {videoLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              {videoError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white p-4">
                  <i className="ri-video-upload-line text-3xl text-gray-500 mb-2"></i>
                  <p className="text-xs text-gray-400 text-center">Video unavailable</p>
                </div>
              )}
              {videoSrc && (
                <video
                  ref={videoRef}
                  src={videoSrc}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  playsInline
                  muted
                  loop
                  autoPlay
                  onEnded={handleVideoEnded}
                  onClick={togglePlay}
                />
              )}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.25) 100%)' }} />
              <button
                onClick={togglePlay}
                className={`absolute inset-0 flex items-center justify-center cursor-pointer group transition-opacity duration-300 ${isPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center transition-all group-hover:scale-110"
                  style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(6px)', border: '1.5px solid rgba(255,255,255,0.35)' }}
                >
                  <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-white text-2xl`}></i>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Animated creator type words */}
        <div
          className="flex flex-col items-start gap-4"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateX(0)' : 'translateX(50px)',
            transition: 'opacity 0.7s ease 0.15s, transform 0.7s cubic-bezier(0.34,1.3,0.64,1) 0.15s',
          }}
        >
          <div className="flex flex-col gap-0.5">
            {creatorTypes.map((item, i) => (
              <div
                key={item.label}
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(30px)',
                  transition: `opacity 0.5s ease ${i * 60 + 200}ms, transform 0.5s cubic-bezier(0.34,1.4,0.64,1) ${i * 60 + 200}ms`,
                }}
              >
                <span
                  className="font-black uppercase leading-none tracking-wide select-none"
                  style={{
                    color: item.color,
                    fontSize: 'clamp(2.5rem, 6vw, 5.5rem)',
                    fontFamily: "'Calibri', 'Segoe UI', 'Candara', 'Arial', sans-serif",
                    letterSpacing: '0.03em',
                    textShadow: `0 0 40px ${item.color}55`,
                  }}
                >
                  {item.label}
                </span>
              </div>
            ))}
          </div>
          <p className="text-gray-500 dark:text-gray-300 text-base max-w-sm leading-relaxed mt-2">
            We&apos;re building for passionate creators across every niche. If you create content and want to monetize your passion, you belong here.
          </p>
          <a
            href="/auth/register"
            className="mt-1 px-10 py-3.5 font-black uppercase tracking-widest text-sm cursor-pointer hover:scale-105 transition-all whitespace-nowrap inline-block text-center"
            style={{
              background: 'linear-gradient(135deg, #a855f7, #ec4899, #f97316)',
              color: 'white',
              borderRadius: '9999px',
              border: 'none',
              fontFamily: "'Calibri', 'Segoe UI', 'Candara', 'Arial', sans-serif",
              letterSpacing: '0.12em',
            }}
          >
            GET STARTED (FREE)
          </a>
        </div>
      </div>

      {/* Divider */}
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="h-px bg-gray-100 dark:bg-gray-800"></div>
      </div>

      {/* ── BOTTOM: Creator niche cards grid ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16">
        <div
          className="text-center mb-10"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease 0.3s, transform 0.6s ease 0.3s',
          }}
        >
          <div className="inline-block px-4 py-1 rounded-full mb-4 bg-purple-100 dark:bg-purple-900/30">
            <span className="text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">Who We&apos;re Building For</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white mb-3">Creators We&apos;re Looking For</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {creatorNiches.map((creator, index) => (
            <div
              key={index}
              className="group rounded-2xl p-5 cursor-pointer hover:-translate-y-1 transition-all duration-300 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
              style={{
                boxShadow: '0 2px 16px rgba(0,0,0,0.05)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
                transition: `opacity 0.5s ease ${index * 50 + 400}ms, transform 0.5s cubic-bezier(0.34,1.4,0.64,1) ${index * 50 + 400}ms`,
              }}
            >
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${creator.gradient} flex items-center justify-center mb-3`}>
                <i className={`${creator.icon} text-xl text-white`}></i>
              </div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1.5 leading-tight">{creator.title}</h3>
              <p className="text-gray-500 dark:text-gray-300 text-xs leading-relaxed mb-2">{creator.description}</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs italic">{creator.examples}</p>
            </div>
          ))}
        </div>

        <div
          className="text-center mt-10"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.6s ease 0.9s',
          }}
        >
          <p className="text-gray-500 dark:text-gray-300 text-sm mb-5">
            Don&apos;t see your niche? <strong className="text-gray-900 dark:text-white">We welcome all creators!</strong>
          </p>
          <a
            href="/auth/register"
            className="px-8 py-3 text-sm font-bold text-white rounded-full hover:scale-105 transition-all whitespace-nowrap cursor-pointer inline-block text-center"
            style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)', border: '1px solid rgba(255,255,255,0.2)' }}
          >
            Join as Creator
          </a>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [howItWorksVideoError, setHowItWorksVideoError] = useState(false);
  const [installOpen, setInstallOpen] = useState(false);
  const [pwaVideoError, setPwaVideoError] = useState(false);
  const [pwaVideoSrc, setPwaVideoSrc] = useState<string | null>(null);
  const [pwaVideoLoading, setPwaVideoLoading] = useState(false);
  const [pwaSectionVisible, setPwaSectionVisible] = useState(false);
  const pwaVideoRef = useRef<HTMLVideoElement>(null);
  const pwaSectionRef = useRef<HTMLElement>(null);
  const { theme, toggleTheme } = useTheme();

  const slides = [
    {
      id: 1,
      creator: 'Lovethe Fro',
      role: 'Fitness Coach',
      earnings: '$8.2K/mo',
      subscribers: '21.3K',
      thumbnail: 'https://storage.readdy-site.link/project_files/f4f0a068-4d6e-4d8b-b9c5-f019c11b8e5c/e1e11c27-0712-4eae-810b-b03ab2ca56e9_compressed_fro1.webp',
      quote: 'I Love interacting with my Streamers',
      tag: 'FITNESS'
    },
    {
      id: 2,
      creator: 'Marcus Rivera',
      role: 'Music Producer',
      earnings: '$9.7K/mo',
      subscribers: '8.3K',
      thumbnail: 'https://readdy.ai/api/search-image?query=Professional%20music%20producer%20man%20in%20recording%20studio%20with%20headphones%20and%20mixing%20equipment%2C%20creative%20workspace%20with%20colorful%20audio%20visualizers%2C%20clean%20minimal%20background%20with%20rich%20amber%20and%20gold%20gradient%20tones%2C%20high%20quality%20studio%20photography%2C%20professional%20lighting%2C%20inspiring%20music%20production%20scene&width=600&height=800&seq=slide2&orientation=portrait',
      quote: 'Finally earning from my passion',
      tag: 'MUSIC'
    },
    {
      id: 3,
      creator: 'Emma Thompson',
      role: 'Digital Artist',
      earnings: '$11.4K/mo',
      subscribers: '15.2K',
      thumbnail: 'https://readdy.ai/api/search-image?query=Professional%20digital%20artist%20woman%20working%20on%20tablet%20with%20stylus%20creating%20colorful%20artwork%2C%20creative%20studio%20space%20with%20art%20displays%2C%20clean%20minimal%20background%20with%20soft%20teal%20and%20mint%20gradient%20tones%2C%20high%20quality%20creative%20photography%2C%20professional%20lighting%2C%20inspiring%20artistic%20scene&width=600&height=800&seq=slide3&orientation=portrait',
      quote: 'My art now supports my lifestyle',
      tag: 'ART'
    },
    {
      id: 4,
      creator: 'Jordan Kim',
      role: 'Lifestyle Creator',
      earnings: '$9.7K/mo',
      subscribers: '21.3K',
      thumbnail: 'https://readdy.ai/api/search-image?query=Young%20lifestyle%20content%20creator%20in%20a%20bright%20modern%20apartment%20with%20natural%20light%2C%20casual%20stylish%20outfit%2C%20relaxed%20confident%20smile%2C%20clean%20minimal%20background%20with%20soft%20rose%20and%20lavender%20gradient%20tones%2C%20high%20quality%20lifestyle%20photography%2C%20professional%20studio%20lighting%2C%20inspiring%20creative%20scene&width=600&height=800&seq=slide4&orientation=portrait',
      quote: 'Built my dream life creating content',
      tag: 'LIFESTYLE'
    },
  ];

  const trendingTopics = [
    {
      id: 1,
      icon: 'ri-robot-2-line',
      title: 'AI Content Assistant',
      description: 'Generate captions, titles, and content ideas with AI',
      status: 'Coming Q2 2026',
      gradient: 'from-purple-500 to-blue-500'
    },
    {
      id: 2,
      icon: 'ri-live-line',
      title: 'Live Streaming',
      description: 'Go live and earn from your streams in real-time',
      status: 'Beta Testing',
      gradient: 'from-red-500 to-pink-500'
    },
    {
      id: 3,
      icon: 'ri-nft-line',
      title: 'NFT Integration',
      description: 'Mint and sell exclusive NFTs to your superfans',
      status: 'Coming Soon',
      gradient: 'from-cyan-500 to-teal-500'
    },
    {
      id: 4,
      icon: 'ri-group-line',
      title: 'Collab Marketplace',
      description: 'Find and partner with other creators for collabs',
      status: 'In Development',
      gradient: 'from-orange-500 to-amber-500'
    },
    {
      id: 5,
      icon: 'ri-bar-chart-grouped-line',
      title: 'Advanced Analytics',
      description: 'Deep insights into audience behavior and trends',
      status: 'Coming Q2 2026',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // ── PWA section: detect scroll into view ──
  useEffect(() => {
    const el = pwaSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPwaSectionVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── PWA video: fetch as blob when section scrolls into view, then auto-play ──
  useEffect(() => {
    if (!pwaSectionVisible) return;
    let cancelled = false;

    const loadAndPlay = async () => {
      setPwaVideoLoading(true);
      try {
        const response = await fetch('https://storage.helloreaddy.io/project_files/f4f0a068-4d6e-4d8b-b9c5-f019c11b8e5c/eb2e413b-ec60-41c5-b992-68056cddc665_Smugg-Download-From-our-Website-1.mp4');
        if (!response.ok) throw new Error('Failed to fetch');
        const blob = await response.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPwaVideoSrc(url);
        setPwaVideoLoading(false);
      } catch {
        if (!cancelled) {
          setPwaVideoError(true);
          setPwaVideoLoading(false);
        }
      }
    };
    loadAndPlay();

    return () => {
      cancelled = true;
    };
  }, [pwaSectionVisible]);

  const handlePrevVideo = () => {
    setCurrentVideoIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % slides.length);
  };



  // ── Mouse parallax for hero blobs ──
  const heroRef = useRef<HTMLElement>(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const mouseTarget = useRef({ x: 0, y: 0 });
  const animFrame = useRef<number>(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const el = heroRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Normalise to -1 … +1 relative to hero centre
      mouseTarget.current = {
        x: ((e.clientX - rect.left) / rect.width - 0.5) * 2,
        y: ((e.clientY - rect.top)  / rect.height - 0.5) * 2,
      };
    };

    // Smooth lerp loop
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    let current = { x: 0, y: 0 };
    const tick = () => {
      current.x = lerp(current.x, mouseTarget.current.x, 0.05);
      current.y = lerp(current.y, mouseTarget.current.y, 0.05);
      setMouse({ x: current.x, y: current.y });
      animFrame.current = requestAnimationFrame(tick);
    };
    animFrame.current = requestAnimationFrame(tick);

    const hero = heroRef.current;
    hero?.addEventListener('mousemove', handleMouseMove);
    return () => {
      hero?.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animFrame.current);
    };
  }, []);

  // ── How It Works scroll animation ──
  const howItWorksSectionRef = useRef<HTMLElement>(null);
  const [howItWorksVisible, setHowItWorksVisible] = useState(false);
  const [stepCounts, setStepCounts] = useState([0, 0, 0, 0]);
  const stepTargets = [1, 2, 3, 4];

  useEffect(() => {
    const el = howItWorksSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHowItWorksVisible(true);
          // Animate counters
          stepTargets.forEach((target, idx) => {
            const delay = idx * 180;
            const duration = 600;
            const start = performance.now();
            const tick = (now: number) => {
              const elapsed = now - start - delay;
              if (elapsed < 0) { requestAnimationFrame(tick); return; }
              const progress = Math.min(elapsed / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              setStepCounts(prev => {
                const next = [...prev];
                next[idx] = Math.round(eased * target);
                return next;
              });
              if (progress < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          });
        } else {
          setHowItWorksVisible(false);
          setStepCounts([0, 0, 0, 0]);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Fan cards scroll animation ──
  const fanSectionRef = useRef<HTMLElement>(null);
  const [fanCardsVisible, setFanCardsVisible] = useState(false);

  useEffect(() => {
    const el = fanSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setFanCardsVisible(true);
        } else {
          // Reset so it re-animates on re-entry
          setFanCardsVisible(false);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);



  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Startup Banner */}
      <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white py-2 px-3 sm:py-3 sm:px-6 text-center">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm">
          <i className="ri-rocket-line text-sm sm:text-lg"></i>
          <span className="font-semibold">We're building the future of creator monetization!</span>
          <span className="hidden md:inline">•</span>
          <span className="text-white/90 hidden sm:inline">Your support helps us grow 💜</span>
        </div>
      </div>

      {/* Download App Banner — Top */}
      <div className="hidden bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 py-2.5 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Download Our App</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-black rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-apple-fill text-white text-sm"></i>
              </div>
              <span className="text-white text-xs font-semibold whitespace-nowrap">App Store</span>
            </button>
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-black rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-android-fill text-white text-sm"></i>
              </div>
              <span className="text-white text-xs font-semibold whitespace-nowrap">Google Play</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`fixed left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'top-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg shadow-md dark:shadow-gray-900/20' : 'top-[36px] sm:top-[44px] bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src="/brands/smuggme/logo.png" 
                alt="Smugg.me" 
                className="h-7 sm:h-8 md:h-9 w-auto object-contain" 
              />
            </Link>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={toggleTheme}
                className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                aria-label="Toggle dark mode"
              >
                {theme === 'dark' ? (
                  <i className="ri-sun-line text-gray-700 dark:text-gray-200 text-lg"></i>
                ) : (
                  <i className="ri-moon-line text-gray-700 dark:text-gray-200 text-lg"></i>
                )}
              </button>
              <Link to="/partner" className="hidden sm:inline-flex px-3 sm:px-5 py-2 text-sm font-bold text-purple-700 dark:text-purple-300 border-2 border-purple-300 dark:border-purple-700 rounded-full hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-400 dark:hover:border-purple-500 transition-all whitespace-nowrap cursor-pointer items-center gap-1.5 relative">
                <i className="ri-vip-crown-line text-sm"></i>
                Smugg.me Verified
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-black uppercase rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white animate-pulse">New</span>
              </Link>
              <a href="/auth/login" className="px-3 sm:px-5 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors whitespace-nowrap cursor-pointer">Login</a>
              <a href="/auth/register" className="px-3 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap flex items-center gap-1 sm:gap-2 cursor-pointer">
                <span>Sign Up</span>
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* NEW: Fanfix-style Hero Section */}
      <FanfixHero />

      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-28 sm:pt-36 md:pt-44 pb-10 sm:pb-16 md:pb-20 px-3 sm:px-6 overflow-hidden bg-[#faf8f5] dark:bg-gray-900">
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIyMjIyMiIgc3Ryb2tlLW9wYWNpdHk9IjAuMDYiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-40 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-5 gap-8 md:gap-12 items-center">
            <div className="md:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 backdrop-blur-sm mb-4 sm:mb-6">
                <div className="w-2 h-2 rounded-full bg-purple-600 animate-pulse"></div>
                <span className="text-xs sm:text-sm font-semibold text-gray-800 dark:text-gray-200">🚀 Early Startup • Join Our Journey</span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white leading-tight mb-4 sm:mb-6">
                Grow your<br />
                Audience, & Convert to a Creator to<br />
                <span className="text-gray-500 dark:text-gray-400">Monetize Your Passion.</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-6 sm:mb-8 max-w-xl leading-relaxed">
                Build your community, share exclusive content, and earn directly from your fans. No middlemen, just you and your supporters. Smugg.me Now allows you to apply directly to become a Creator if you meet the minimum of 2K Followers
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                <button className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap text-center cursor-pointer">
                  Start Earning Free
                </button>
                <a href="#simple-process" className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold text-gray-700 dark:text-gray-300 border-2 border-gray-300 dark:border-gray-600 rounded-full hover:border-gray-500 dark:hover:border-gray-400 transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer">
                  How It Works
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex -space-x-2 sm:-space-x-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white dark:border-gray-700 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                      <i className="ri-user-line text-gray-500 dark:text-gray-400 text-xs sm:text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                  <strong className="text-gray-900 dark:text-white">Earn your potential</strong> — Real Followers, Real People, <strong className="text-gray-900 dark:text-white">Real Earning</strong>
                </p>
              </div>
            </div>
            <div className="md:col-span-2 mt-6 md:mt-0">
              <div className="relative">
                <div className="absolute inset-0 bg-white/10 dark:bg-gray-800/10 rounded-3xl blur-3xl"></div>
                <div className="relative bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/40 dark:border-gray-700/40">
                  {/* Image Slider */}
                  <div className="relative aspect-[3/4] bg-gray-900 overflow-hidden">
                    {slides.map((slide, index) => (
                      <div
                        key={slide.id}
                        className={`absolute inset-0 transition-all duration-700 ${
                          index === currentVideoIndex
                            ? 'opacity-100 scale-100'
                            : 'opacity-0 scale-95 pointer-events-none'
                        }`}
                      >
                        <img
                          src={slide.thumbnail}
                          alt={slide.creator}
                          className="w-full h-full object-cover object-top"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                        {/* Creator Info Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center border-2 border-white">
                              <i className="ri-user-line text-white text-xl"></i>
                            </div>
                            <div>
                              <h3 className="text-white font-bold text-lg">{slide.creator}</h3>
                              <p className="text-white/80 text-sm">{slide.role}</p>
                            </div>
                          </div>
                          <p className="text-white text-lg font-semibold mb-3 italic">"{slide.quote}"</p>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-white">
                              <i className="ri-user-follow-line text-purple-300"></i>
                              <span className="font-bold">{slide.subscribers}</span>
                            </div>
                          </div>
                        </div>

                        {/* Tag Badge */}
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full tracking-wider">
                            {slide.tag}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Navigation Arrows */}
                    <button
                      onClick={handlePrevVideo}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center hover:bg-white/30 transition-all z-10 cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-2xl text-white"></i>
                    </button>
                    <button
                      onClick={handleNextVideo}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm border border-white/40 flex items-center justify-center hover:bg-white/30 transition-all z-10 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-2xl text-white"></i>
                    </button>

                    {/* Dots Indicator */}
                    <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                      {slides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentVideoIndex(index)}
                          className={`transition-all cursor-pointer ${
                            index === currentVideoIndex
                              ? 'w-8 h-2 bg-white rounded-full'
                              : 'w-2 h-2 bg-white/50 rounded-full hover:bg-white/70'
                          }`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Stats Bar */}
                  <div className="bg-white dark:bg-gray-800 p-4 md:p-6">
                    <div className="grid grid-cols-3 gap-2 md:gap-4">
                      <div className="text-center">
                        <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                          {slides[currentVideoIndex].subscribers}
                        </div>
                        <div className="text-[10px] md:text-xs font-bold text-gray-600 dark:text-gray-300 whitespace-nowrap">Subscribers</div>
                      </div>
                      <div className="flex flex-col items-center justify-center">
                        <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-1.5 flex-wrap">
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/0/09/YouTube_full-color_icon_%282017%29.svg"
                            alt="YouTube"
                            className="w-4 h-4 md:w-6 md:h-6 object-contain flex-shrink-0"
                          />
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/b/b9/2023_Facebook_icon.svg"
                            alt="Facebook"
                            className="w-4 h-4 md:w-6 md:h-6 object-contain flex-shrink-0"
                          />
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/e/e7/Instagram_logo_2016.svg"
                            alt="Instagram"
                            className="w-4 h-4 md:w-6 md:h-6 object-contain flex-shrink-0"
                          />
                          <img
                            src="https://upload.wikimedia.org/wikipedia/commons/c/ce/X_logo_2023.svg"
                            alt="X (Twitter)"
                            className="w-3.5 h-3.5 md:w-5 md:h-5 object-contain flex-shrink-0"
                          />
                        </div>
                        <div className="text-[10px] md:text-xs font-bold text-gray-600 dark:text-gray-300 whitespace-nowrap">Bring Your Fans</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">92%</div>
                        <div className="text-[10px] md:text-xs font-bold text-gray-600 dark:text-gray-300 whitespace-nowrap">Revenue Share</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Revenue Share Comparison Section */}
      <section className="py-10 sm:py-16 px-4 sm:px-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a21caf 35%, #db2777 65%, #ec4899 80%, #818cf8 100%)' }}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA4IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40 pointer-events-none"></div>
        {/* Decorative glow orbs */}
        <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%)' }}></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.10) 0%, transparent 70%)' }}></div>

        <div className="max-w-6xl mx-auto relative z-10">

          {/* ── Two-column layout: big number LEFT, comparisons RIGHT ── */}
          <div className="grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-16 items-center">

            {/* LEFT — 94% hero + label */}
            <div className="flex flex-col items-center text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 border border-white/30 backdrop-blur-sm mb-4">
                <i className="ri-money-dollar-circle-line text-white text-sm"></i>
                <span className="text-xs font-bold text-white uppercase tracking-wider">Creator Revenue</span>
              </div>
              <div
                className="font-black leading-none text-white select-none"
                style={{ fontSize: 'clamp(5rem, 14vw, 10rem)', textShadow: '0 0 80px rgba(255,255,255,0.25)' }}
              >
                92%
              </div>
              <p className="text-white/80 text-sm mt-2 max-w-[200px]">you keep — lowest fee in the industry</p>
              <a href="/auth/register" className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-white rounded-full text-orange-500 font-bold text-sm hover:scale-105 hover:shadow-xl transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-rocket-line"></i>
                Claim Your Rate
              </a>
              <p className="text-white/40 text-xs mt-2">Excl. third-party fees. T&amp;Cs apply.</p>
            </div>

            {/* RIGHT — compact platform comparison table */}
            <div className="flex flex-col gap-2.5 w-full">
              <p className="text-white/70 text-xs font-semibold uppercase tracking-widest mb-1">How we compare</p>

              {[
                { name: 'Smugg.me',              pct: 92, fee: 'Only 8% fee',           icon: 'ri-rocket-line',      highlight: true  },
                { name: 'Patreon',             pct: 88, fee: '8–12% fee',             icon: 'ri-heart-2-line',     highlight: false },
                { name: 'Fanfix',            pct: 80, fee: '20% fee',               icon: 'ri-image-line',       highlight: false },
                { name: 'YouTube AdSense',     pct: 55, fee: 'Ad revenue only',       icon: 'ri-youtube-line',     highlight: false },
                { name: 'Twitch',              pct: 50, fee: '50% split',             icon: 'ri-live-line',        highlight: false },
                { name: 'TikTok Creator Fund', pct: 4,  fee: '~$0.02–0.04 per 1K',   icon: 'ri-tiktok-line',      highlight: false },
              ].map((p) => (
                <div
                  key={p.name}
                  className={`flex flex-wrap items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl transition-all ${
                    p.highlight
                      ? 'bg-white/25 border-2 border-white/60 backdrop-blur-sm'
                      : 'bg-white/10 border border-white/20'
                  }`}
                >
                  <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    p.highlight ? 'bg-white/40' : 'bg-white/15'
                  }`}>
                    <i className={`${p.icon} text-white text-xs sm:text-sm`}></i>
                  </div>
                  <span className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${
                    p.highlight ? 'text-white' : 'text-white/80'
                  }`}>{p.name}</span>
                  {p.highlight && (
                    <span className="ml-1 px-2 py-0.5 text-[10px] font-black text-orange-500 bg-white rounded-full uppercase tracking-wide whitespace-nowrap">YOU</span>
                  )}
                  <span className="ml-auto text-white/50 text-xs whitespace-nowrap hidden sm:block">{p.fee}</span>
                  <div className="w-20 sm:w-24 bg-white/15 rounded-full h-1.5 overflow-hidden flex-shrink-0">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        p.highlight ? 'bg-white' : 'bg-white/50'
                      }`}
                      style={{ width: `${p.pct}%` }}
                    ></div>
                  </div>
                  <span className={`text-xs sm:text-sm font-black w-8 sm:w-9 text-right flex-shrink-0 ${
                    p.highlight ? 'text-white' : 'text-white/70'
                  }`}>{p.pct}%</span>
                </div>
              ))}

              {/* Math callout — big statement */}
              <div className="mt-6 bg-white/20 border border-white/30 rounded-2xl px-6 py-6 md:px-8 md:py-7 flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6 text-center flex-wrap">
                <span className="text-white/80 text-base md:text-lg font-medium">
                  Earn <strong className="text-white font-black text-xl md:text-2xl">$5K/mo</strong>
                </span>
                <span className="hidden md:block text-white/40 text-2xl font-light">→</span>
                <span className="text-white font-black text-2xl md:text-3xl tracking-tight">
                  Take home $4,600
                </span>
                <span className="text-white/90 font-bold text-lg md:text-xl bg-white/15 px-4 py-1.5 rounded-full border border-white/30">
                  with Smugg.me
                </span>
                <span className="text-white/40 text-sm md:text-base font-medium">
                  vs only $2,500 on Twitch
                </span>
                <p className="w-full text-white/50 text-xs mt-2">
                  *Before Stripe / PayPal processing fees (~2.9% + 30¢ per transaction)
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fan Support Showcase Section */}
      <section ref={fanSectionRef} className="w-full bg-white dark:bg-gray-900 py-16 sm:py-20 px-6 sm:px-12 lg:px-24 relative overflow-hidden">
        {/* Soft lavender blob accent behind cards */}
        <div className="absolute left-[15%] top-1/2 -translate-y-1/2 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(196,181,253,0.30) 0%, transparent 70%)' }}></div>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-24 items-center">

          {/* LEFT — Fan message cards */}
          <div className="relative flex flex-col gap-4">

            {/* Card 1 */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 cursor-pointer hover:-translate-y-1 hover:scale-[1.02]"
              style={{
                boxShadow: '0 2px 20px rgba(0,0,0,0.07)',
                transform: fanCardsVisible ? 'translateX(0) scale(1)' : 'translateX(-60px) scale(0.95)',
                opacity: fanCardsVisible ? 1 : 0,
                transition: 'transform 0.55s cubic-bezier(0.34,1.56,0.64,1), opacity 0.45s ease',
                transitionDelay: '0ms',
              }}
            >
              <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Young%20man%20casual%20portrait%20with%20short%20dark%20hair%2C%20friendly%20smile%2C%20neutral%20light%20gray%20background%2C%20clean%20headshot%20photo%2C%20realistic%20professional%20portrait%20photography%2C%20soft%20studio%20lighting%2C%20modern%20casual%20style&width=100&height=100&seq=fancard1&orientation=squarish"
                  alt="Lostsouls3"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-gray-900 dark:text-white text-sm">Lostsouls3</span>
                  <span className="font-bold text-purple-600 text-sm">$60</span>
                  <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></span>
                </div>
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
                  <i className="ri-message-2-line text-gray-400"></i>
                  <span>Hello there I was...</span>
                </div>
                <div className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">5 days ago</div>
              </div>
            </div>

            {/* Card 2 — offset right */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 ml-8 cursor-pointer hover:-translate-y-1 hover:scale-[1.02]"
              style={{
                boxShadow: '0 2px 20px rgba(0,0,0,0.07)',
                transform: fanCardsVisible ? 'translateX(0) scale(1)' : 'translateX(-60px) scale(0.95)',
                opacity: fanCardsVisible ? 1 : 0,
                transition: 'transform 0.55s cubic-bezier(0.34,1.56,0.64,1), opacity 0.45s ease',
                transitionDelay: '130ms',
              }}
            >
              <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Middle%20aged%20man%20professional%20portrait%2C%20grey%20hair%2C%20warm%20smile%2C%20neutral%20white%20background%2C%20clean%20corporate%20headshot%20photo%2C%20realistic%20studio%20photography%2C%20soft%20lighting%2C%20business%20casual%20appearance%2C%20approachable%20expression&width=100&height=100&seq=fancard2&orientation=squarish"
                  alt="Georges53"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-gray-900 dark:text-white text-sm">Georges53</span>
                  <span className="font-bold text-purple-600 text-sm">$100</span>
                  <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></span>
                </div>
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
                  <i className="ri-gift-line text-gray-400"></i>
                  <span>💰 Paid $100 tip...</span>
                </div>
                <div className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">5 days ago</div>
              </div>
            </div>

            {/* Card 3 */}
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl px-5 py-4 flex items-center gap-4 cursor-pointer hover:-translate-y-1 hover:scale-[1.02]"
              style={{
                boxShadow: '0 2px 20px rgba(0,0,0,0.07)',
                transform: fanCardsVisible ? 'translateX(0) scale(1)' : 'translateX(-60px) scale(0.95)',
                opacity: fanCardsVisible ? 1 : 0,
                transition: 'transform 0.55s cubic-bezier(0.34,1.56,0.64,1), opacity 0.45s ease',
                transitionDelay: '260ms',
              }}
            >
              <div className="w-12 h-12 rounded-full flex-shrink-0 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Young%20woman%20portrait%20with%20blonde%20hair%2C%20casual%20friendly%20smile%2C%20neutral%20light%20background%2C%20clean%20lifestyle%20headshot%20photo%2C%20realistic%20photography%2C%20soft%20natural%20lighting%2C%20approachable%20and%20warm%20expression%2C%20modern%20style&width=100&height=100&seq=fancard3&orientation=squarish"
                  alt="Megme9"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-bold text-gray-900 dark:text-white text-sm">Megme9___</span>
                  <span className="font-bold text-purple-600 text-sm">$10</span>
                  <span className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></span>
                </div>
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs">
                  <i className="ri-lock-line text-gray-400"></i>
                  <span>💰 Unlocked Content...</span>
                </div>
                <div className="text-gray-400 dark:text-gray-500 text-xs mt-0.5">5 days ago</div>
              </div>
            </div>
          </div>

          {/* RIGHT — Text content */}
          <div className="flex flex-col justify-center">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-purple-700 dark:text-purple-400 uppercase leading-tight mb-5">
              ALLOW YOUR FANS TO SUPPORT YOUR WORK
            </h2>
            <h3
              className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-tight mb-8 dark:text-white"
              style={{ background: 'linear-gradient(135deg, #a855f7 0%, #93c5fd 60%, #7dd3fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
            >
              MONETIZE YOUR&nbsp; CONTENT &amp; Live Stream WITH FANs, SUBSCRIPTIONS or not, We Have Think Pay Per View!!
            </h3>
            <img
              src="/brands/smuggme/logo.png"
              alt="Smugg.me"
              className="h-12 w-auto object-contain"
            />
          </div>
        </div>

        {/* Unicorn bottom-right */}
        <div className="absolute bottom-5 right-8 text-5xl select-none pointer-events-none" aria-hidden="true">
          🦄
        </div>
      </section>

      {/* How It Works Section */}
      <section id="simple-process" ref={howItWorksSectionRef} className="w-full bg-gradient-to-br from-purple-50/60 via-white to-pink-50/40 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 py-20 sm:py-28 px-6 sm:px-12 overflow-hidden">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div
            className="text-center mb-16 sm:mb-20"
            style={{
              opacity: howItWorksVisible ? 1 : 0,
              transform: howItWorksVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
            }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-purple-100 mb-5">
              <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse"></div>
              <span className="text-xs font-bold uppercase tracking-widest bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Simple Process</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 dark:text-white leading-tight mb-4">
              Start Earning in
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"> 4 Steps</span>
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-300 max-w-xl mx-auto leading-relaxed">
              No technical skills needed. Set up your creator page and start earning from your fans today.
            </p>
          </div>

          {/* Steps */}
          <div className="relative">
            {/* Connector line — desktop only */}
            <div
              className="hidden lg:block absolute top-[52px] left-[12.5%] right-[12.5%] h-0.5 pointer-events-none"
              style={{
                background: 'linear-gradient(to right, #a855f7, #ec4899, #f97316)',
                opacity: howItWorksVisible ? 0.35 : 0,
                transition: 'opacity 0.8s ease 0.4s',
              }}
            ></div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
              {[
                {
                  step: 1,
                  icon: 'ri-user-star-line',
                  title: 'Create Your Profile',
                  desc: 'Set up your creator page in minutes. Add your bio, profile photo, and customise your branding.',
                  accent: 'from-purple-500 to-purple-600',
                  delay: 0,
                },
                {
                  step: 2,
                  icon: 'ri-upload-cloud-2-line',
                  title: 'Share Your Content',
                  desc: 'Post videos, photos, audio, or written content. Decide what\'s free and what\'s subscriber-only.',
                  accent: 'from-pink-500 to-pink-600',
                  delay: 180,
                },
                {
                  step: 3,
                  icon: 'ri-group-2-line',
                  title: 'Grow Your Audience',
                  desc: 'Fans subscribe, tip, and unlock exclusive content. Build a loyal community around your passion.',
                  accent: 'from-rose-500 to-orange-500',
                  delay: 360,
                },
                {
                  step: 4,
                  icon: 'ri-money-dollar-circle-line',
                  title: 'Get Paid — 92%',
                  desc: 'Receive payouts directly. Creators keep 92% of every dollar their fans spend. No surprises.',
                  accent: 'from-orange-500 to-amber-500',
                  delay: 540,
                },
              ].map((s, i) => (
                <div
                  key={s.step}
                  style={{
                    opacity: howItWorksVisible ? 1 : 0,
                    transform: howItWorksVisible ? 'translateY(0) scale(1)' : 'translateY(48px) scale(0.94)',
                    transition: `opacity 0.55s ease ${s.delay}ms, transform 0.55s cubic-bezier(0.34,1.4,0.64,1) ${s.delay}ms`,
                  }}
                >
                  <div className="group flex flex-col items-center text-center p-8 rounded-3xl bg-white dark:bg-gray-800 hover:-translate-y-2 transition-transform duration-300 cursor-default" style={{ boxShadow: '0 4px 28px rgba(0,0,0,0.07)' }}>

                    {/* Step number ring */}
                    <div className="relative mb-6">
                      <div className={`w-[104px] h-[104px] rounded-full bg-gradient-to-br ${s.accent} flex items-center justify-center`}>
                        <span className="text-4xl font-black text-white tabular-nums">
                          {stepCounts[i]}
                        </span>
                      </div>
                      {/* Pulse ring */}
                      <div
                        className={`absolute inset-0 rounded-full bg-gradient-to-br ${s.accent} opacity-0 group-hover:opacity-20 group-hover:scale-125 transition-all duration-500`}
                      ></div>
                    </div>

                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${s.accent} flex items-center justify-center mb-4`}>
                      <i className={`${s.icon} text-xl text-white`}></i>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">{s.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-300 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom CTA strip */}
          <div
            className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4"
            style={{
              opacity: howItWorksVisible ? 1 : 0,
              transform: howItWorksVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.8s, transform 0.6s ease 0.8s',
            }}
          >
            <a
              href="/auth/register"
              className="px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:scale-105 hover:shadow-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-3 inline-block"
            >
              <i className="ri-rocket-line text-lg"></i>
              Get Started Free
            </a>
            <p className="text-sm text-gray-400 dark:text-gray-500">No credit card needed &nbsp;·&nbsp; Setup in under 5 minutes</p>
          </div>
        </div>
      </section>

      {/* PWA Install Guide Video Section */}
      <section ref={pwaSectionRef} className="w-full bg-white dark:bg-gray-900 py-16 sm:py-24 px-6 sm:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-100 mb-4">
              <i className="ri-smartphone-line text-purple-600"></i>
              <span className="text-xs font-bold uppercase tracking-wider text-purple-600">Install in Seconds</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-3">
              Get the App on Your Phone
            </h2>
            <p className="text-gray-500 dark:text-gray-300 text-base sm:text-lg max-w-2xl mx-auto">
              No app store needed. Install Smugg.me directly to your home screen as a Progressive Web App.
            </p>
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 text-base font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:shadow-xl hover:scale-105 transition-all cursor-pointer"
            >
              <i className="ri-download-2-line text-lg"></i>
              Install the App
            </button>
          </div>

          {/* Video Player */}
          <div className="relative max-w-3xl mx-auto rounded-3xl overflow-hidden bg-gray-900 shadow-2xl">
            <div className="aspect-video relative">
              {pwaVideoError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white">
                  <i className="ri-video-upload-line text-5xl text-purple-400 mb-4"></i>
                  <p className="text-lg font-semibold mb-1">Video Coming Soon</p>
                  <p className="text-sm text-gray-400">Check back for the full tutorial</p>
                  <button
                    onClick={() => { setPwaVideoError(false); setPwaVideoLoading(false); setPwaVideoSrc(null); }}
                    className="mt-6 px-5 py-2 text-sm font-semibold bg-white/10 border border-white/20 rounded-full hover:bg-white/20 transition-all cursor-pointer"
                  >
                    Retry
                  </button>
                </div>
              ) : pwaVideoLoading || !pwaVideoSrc ? (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                  <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : (
                <video
                  ref={pwaVideoRef}
                  className="w-full h-full"
                  controls
                  autoPlay
                  muted
                  playsInline
                  src={pwaVideoSrc || ''}
                  onError={() => setPwaVideoError(true)}
                />
              )}
            </div>
          </div>

          {/* Install Steps */}
          <div className="grid sm:grid-cols-2 gap-6 mt-10 max-w-3xl mx-auto">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center">
                  <i className="ri-apple-fill text-white"></i>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white">iPhone &amp; iPad</h3>
              </div>
              <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-start gap-2"><span className="font-bold text-purple-600">1.</span> Tap the Share button in Safari</li>
                <li className="flex items-start gap-2"><span className="font-bold text-purple-600">2.</span> Scroll down and tap &quot;Add to Home Screen&quot;</li>
                <li className="flex items-start gap-2"><span className="font-bold text-purple-600">3.</span> Tap &quot;Add&quot; — you&apos;re done!</li>
              </ol>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                  <i className="ri-android-fill text-white"></i>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white">Android</h3>
              </div>
              <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <li className="flex items-start gap-2"><span className="font-bold text-purple-600">1.</span> Tap the menu (⋮) in Chrome</li>
                <li className="flex items-start gap-2"><span className="font-bold text-purple-600">2.</span> Tap &quot;Add to Home screen&quot; or &quot;Install app&quot;</li>
                <li className="flex items-start gap-2"><span className="font-bold text-purple-600">3.</span> Tap &quot;Install&quot; — you&apos;re done!</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Showcase - Creator Types Section */}
      <PlatformCreatorSection onGetStarted={() => {}} />

      {/* Real Creators, Real Content Section - NEW */}
      <section className="py-24 px-6 bg-gradient-to-br from-white via-purple-50/30 to-pink-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white mb-6">
              <i className="ri-shield-check-line text-xl"></i>
              <span className="text-sm font-bold uppercase tracking-wider">Our Commitment</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Real Creators.<br />
              Real Content.<br />
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Real Connections.</span>
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We&apos;re building a platform for <strong>authentic creators</strong> who create <strong>genuine content</strong>. No filters, no AI-generated content, no fake personas. Just real people sharing their real passions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border-2 border-purple-100 dark:border-purple-900 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">100% Human Created</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Every piece of content on our platform is created by real people. We don&apos;t allow AI-generated content or automated posts. Your audience deserves authenticity.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border-2 border-pink-100 dark:border-pink-900 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-pink-600 flex items-center justify-center mb-6">
                <i className="ri-camera-off-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Fake Filters</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                We celebrate real beauty and authenticity. Share your true self without pressure to use heavy filters or unrealistic editing. Be you, unapologetically.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border-2 border-orange-100 dark:border-orange-900 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center mb-6">
                <i className="ri-heart-3-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Genuine Connections</h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Build real relationships with your audience. No bots, no fake followers, no inflated numbers. Just authentic fans who truly care about your content.
              </p>
            </div>
          </div>

          {/* Gradient border wrapper */}
          <div className="rounded-3xl p-[3px]" style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899, #f97316)' }}>
            <div className="bg-white dark:bg-gray-800 rounded-[22px] p-12 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899, #f97316)' }}>
                <i className="ri-verified-badge-line text-white text-xl"></i>
                <span className="text-sm font-bold text-white uppercase tracking-wider">Our Promise</span>
              </div>
              <h3 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
                We Verify Every Creator
              </h3>
              <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
                To maintain authenticity, we manually review every creator application. We&apos;re committed to keeping this platform a space for <strong className="text-gray-900 dark:text-white">real people creating real content</strong>. No spam, no bots, no fake accounts.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 text-gray-600 dark:text-gray-300">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                  <span>Manual verification</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                  <span>Real identity checks</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
                  <span>Content authenticity</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-semibold uppercase tracking-wider mb-4">Platform Features</div>
            <h2 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need<br />To Grow & Earn
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Powerful tools designed to help creators build sustainable income streams and meaningful connections with their audience.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-money-dollar-circle-line',
                gradient: 'from-purple-500 to-purple-600',
                title: 'Multiple Revenue Streams',
                description: 'Earn through subscriptions, pay-per-post, tips, and exclusive content. Diversify your income with flexible monetization options.'
              },
              {
                icon: 'ri-vip-crown-line',
                gradient: 'from-pink-500 to-pink-600',
                title: 'Subscriber Tiers',
                description: 'Create multiple membership levels with unique perks and pricing. Reward your most loyal fans with exclusive benefits.'
              },
              {
                icon: 'ri-file-list-3-line',
                gradient: 'from-orange-500 to-orange-600',
                title: 'Content Flexibility',
                description: 'Share videos, images, text posts, audio, and more. Choose what\'s free and what\'s premium for each piece of content.'
              },
              {
                icon: 'ri-line-chart-line',
                gradient: 'from-pink-500 to-pink-600',
                title: 'Real-Time Analytics',
                description: 'Track your earnings, subscriber growth, and content performance with detailed insights and actionable metrics.'
              },
              {
                icon: 'ri-chat-heart-line',
                gradient: 'from-orange-500 to-orange-600',
                title: 'Direct Fan Connection',
                description: 'Build genuine relationships through comments, DMs, polls, and community posts. Your audience, your rules.'
              },
              {
                icon: 'ri-shield-check-line',
                gradient: 'from-purple-500 to-purple-600',
                title: 'Secure Payments',
                description: 'Fast, secure payouts with transparent fees. Get paid on time, every time, with industry-leading payment protection.'
              }
            ].map((feature, index) => (
              <div key={index} className="group bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6`}>
                  <i className={`${feature.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{feature.description}</p>
                <a href="#" className="inline-flex items-center gap-2 text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent group-hover:gap-3 transition-all">
                  Learn more
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6" style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #a21caf 35%, #db2777 65%, #ec4899 80%, #818cf8 100%)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 mb-6">
            <i className="ri-heart-fill text-white"></i>
            <span className="text-sm font-semibold text-white">Built by Creators, for Creators</span>
          </div>
          <h2 className="text-5xl font-bold text-white mb-6">
            We&apos;re Just Getting Started
          </h2>
          <p className="text-xl text-white/90 leading-relaxed mb-8">
            We&apos;re building this platform with passion and dedication to empower creators like you. Every creator who joins, every piece of feedback, and every success story helps us improve and grow. We&apos;re in this together! 🚀
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-feedback-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Your Voice Matters</h3>
              <p className="text-white/80 text-sm">We actively listen to creator feedback and implement features you need</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Growing Together</h3>
              <p className="text-white/80 text-sm">Early supporters get priority access to new features and updates</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-sparkling-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Transparent Journey</h3>
              <p className="text-white/80 text-sm">We share our progress, challenges, and wins openly with our community</p>
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <p className="text-lg text-white/90 mb-4">
              <strong className="text-white">💜 Thank you for believing in us!</strong> Your support as an early adopter means everything. Together, we&apos;re creating a platform where creators truly thrive.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <a href="/auth/register" className="px-8 py-3 text-base font-semibold text-gray-900 bg-white rounded-full hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap inline-block text-center">
                Join as Early Creator
              </a>
              <button className="px-8 py-3 text-base font-semibold text-white border-2 border-white/40 rounded-full hover:border-white/70 transition-all whitespace-nowrap">
                Share Feedback
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Services Section */}
      <section className="py-16 px-6 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Trusted By Industry Leaders</p>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Powered by World-Class Infrastructure</h3>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-20">
            {/* AWS Logo */}
            <div className="flex flex-col items-center gap-2 group cursor-pointer">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg"
                alt="Amazon Web Services"
                className="h-10 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity dark:brightness-0 dark:invert"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Verified Partner</span>
            </div>

            {/* Stripe Logo */}
            <div className="flex flex-col items-center gap-2 group cursor-pointer">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg"
                alt="Stripe Payments"
                className="h-8 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity dark:brightness-0 dark:invert"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Secure Payments</span>
            </div>

            {/* PayPal Logo */}
            <div className="flex flex-col items-center gap-2 group cursor-pointer">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg"
                alt="PayPal"
                className="h-8 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity dark:brightness-0 dark:invert"
              />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Buyer Protection</span>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8">
            Your data and payments are protected by industry-leading security standards
          </p>
        </div>
      </section>

      {/* How It Works Video Modal */}
      {howItWorksOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setHowItWorksOpen(false)}
        >
          <div
            className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setHowItWorksOpen(false)}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 transition-all cursor-pointer"
            >
              <i className="ri-close-line text-white text-xl"></i>
            </button>

            {/* Video */}
            {howItWorksVideoError ? (
              <div className="w-full aspect-video bg-gray-950 flex flex-col items-center justify-center text-white">
                <i className="ri-video-upload-line text-5xl text-purple-400 mb-4"></i>
                <p className="text-lg font-semibold mb-1">Video Coming Soon</p>
                <p className="text-sm text-gray-400">Check back for the full walkthrough</p>
              </div>
            ) : (
              <video
                className="w-full aspect-video"
                controls
                autoPlay
                src="/videos/how-it-works.mp4"
                onError={() => setHowItWorksVideoError(true)}
              >
                Your browser does not support the video tag.
              </video>
            )}

            {/* Caption */}
            <div className="px-6 py-4 bg-gray-950 flex items-center justify-between">
              <div>
                <p className="text-white font-semibold text-base">How Smugg.me Works</p>
                <p className="text-gray-400 text-sm">See how creators earn directly from their fans</p>
              </div>
              <button className="px-5 py-2 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-full hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap cursor-pointer">
                Sign Up
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gradient-to-b from-gray-900 to-black text-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Startup Message in Footer */}
          <div className="text-center mb-12 pb-12 border-b border-gray-700">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-4">
              <i className="ri-seedling-line text-green-400"></i>
              <span className="text-sm font-semibold">Early Stage Startup</span>
            </div>
            <p className="text-gray-400 dark:text-gray-500 max-w-2xl mx-auto leading-relaxed">
              We&apos;re a passionate team building the creator economy of tomorrow. Your support, patience, and feedback as we grow means the world to us. Thank you for being part of our journey! 🙏
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <img src="/brands/smuggme/logo.png" alt="Smugg.me Logo" className="h-14 w-auto mb-4" />
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Empowering creators to build sustainable income and meaningful connections with their audience.
              </p>
              <div className="flex gap-3">
                <a href="https://x.com/SmuggmeSocial" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 hover:bg-clip-text hover:text-transparent transition-all flex items-center justify-center">
                  <i className="ri-twitter-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/smuggme.social/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/10 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 hover:bg-clip-text hover:text-transparent transition-all flex items-center justify-center">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Legal</h4>
              <ul className="space-y-3">
                <li>
                  <a href="/page/terms-of-service" className="text-white hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 hover:bg-clip-text hover:text-transparent transition-all">Terms</a>
                </li>
                <li>
                  <a href="/page/privacy-policy" className="text-white hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 hover:bg-clip-text hover:text-transparent transition-all">Privacy</a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">Stay Updated</h4>
              <div className="relative">
                <input type="email" placeholder="Your email" className="w-full px-4 py-3 bg-transparent border-b border-gray-700 focus:border-purple-500 outline-none text-white placeholder-gray-500 transition-colors" />
                <button className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center hover:shadow-lg transition-all">
                  <i className="ri-arrow-right-line text-white"></i>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-3">We respect your privacy</p>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-400 dark:text-gray-500">© 2026 Smugg.me. All rights reserved. • Made with 💜 by the Smuggme LLC family</p>
              <a href="/" className="text-sm text-gray-400 dark:text-gray-500 hover:text-white transition-colors">
                Powered by Smuggme LLC
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Download App Banner — Bottom */}
      <div className="hidden bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 py-4 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
          <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">Download Our App</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black rounded-xl hover:bg-gray-800 hover:scale-105 transition-all cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-apple-fill text-white text-base"></i>
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-[9px] text-gray-400">Download on the</span>
                <span className="text-white text-xs font-bold whitespace-nowrap">App Store</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-black rounded-xl hover:bg-gray-800 hover:scale-105 transition-all cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-android-fill text-white text-base"></i>
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-[9px] text-gray-400">Get it on</span>
                <span className="text-white text-xs font-bold whitespace-nowrap">Google Play</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Floating Get the App Button */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-white">
              <img
                src="/brands/smuggme/logo.png"
                alt="Smugg.me App"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Get the App</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">Better experience on mobile</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              className="w-9 h-9 bg-black rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
              aria-label="Install on iPhone"
            >
              <i className="ri-apple-fill text-white text-base"></i>
            </button>
            <button
              type="button"
              onClick={() => setInstallOpen(true)}
              className="w-9 h-9 bg-black rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer"
              aria-label="Install on Android"
            >
              <i className="ri-android-fill text-white text-base"></i>
            </button>
          </div>
        </div>
      </div>

      <InstallPwaModal
        open={installOpen}
        onClose={() => setInstallOpen(false)}
        appName="Smugg.me"
      />
    </div>
  );
}
